import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { siteTexts } from "../i18n/siteTexts";
import "./InfoTooltip.css";

type InfoTooltipProps = {
  content: string;
  ariaLabel?: string;
  className?: string;
};

export default function InfoTooltip({
  ariaLabel,
  className = "",
  content,
}: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});
  const [placement, setPlacement] = useState<"top" | "bottom" | "left" | "right">(
    "bottom",
  );
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLSpanElement | null>(null);
  const tooltipId = useId();
  const label = ariaLabel || siteTexts.common.infoTooltip.ariaLabel;
  const safeContent = String(content || "").trim();

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 12;
    const gap = 8;
    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const popoverWidth = popoverRect.width;
    const popoverHeight = popoverRect.height;
    const availableTop = triggerRect.top;
    const availableBottom = viewportHeight - triggerRect.bottom;
    const availableLeft = triggerRect.left;
    const availableRight = viewportWidth - triggerRect.right;

    const nextPlacement =
      availableTop >= popoverHeight + gap + margin
        ? "top"
        : availableBottom >= popoverHeight + gap + margin
          ? "bottom"
          : availableRight >= popoverWidth + gap + margin
            ? "right"
            : availableLeft >= popoverWidth + gap + margin
              ? "left"
              : "bottom";

    let top = triggerRect.bottom + gap;
    let left = triggerRect.left + triggerRect.width / 2 - popoverWidth / 2;

    if (nextPlacement === "top") {
      top = triggerRect.top - popoverHeight - gap;
    } else if (nextPlacement === "left") {
      top = triggerRect.top + triggerRect.height / 2 - popoverHeight / 2;
      left = triggerRect.left - popoverWidth - gap;
    } else if (nextPlacement === "right") {
      top = triggerRect.top + triggerRect.height / 2 - popoverHeight / 2;
      left = triggerRect.right + gap;
    }

    const maxLeft = Math.max(margin, viewportWidth - popoverWidth - margin);
    const maxTop = Math.max(margin, viewportHeight - popoverHeight - margin);
    left = Math.min(Math.max(margin, left), maxLeft);
    top = Math.min(Math.max(margin, top), maxTop);

    setPlacement(nextPlacement);
    setPopoverStyle({ left, top });
  }, []);

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        !rootRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("touchstart", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("touchstart", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const frameId = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(frameId);
  }, [open, safeContent, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handlePositionChange = () => updatePosition();
    window.addEventListener("resize", handlePositionChange);
    window.addEventListener("scroll", handlePositionChange, true);
    return () => {
      window.removeEventListener("resize", handlePositionChange);
      window.removeEventListener("scroll", handlePositionChange, true);
    };
  }, [open, updatePosition]);

  if (!safeContent) return null;

  return (
    <span
      ref={rootRef}
      className={`info-tooltip${open ? " is-open" : ""}${
        className ? ` ${className}` : ""
      }`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="info-tooltip-trigger"
        aria-label={label}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        i
      </button>
      {open &&
        createPortal(
          <span
            ref={popoverRef}
            id={tooltipId}
            className="info-tooltip-popover"
            data-placement={placement}
            role="tooltip"
            style={popoverStyle}
          >
            {safeContent}
          </span>,
          document.body,
        )}
    </span>
  );
}
